import { z } from 'zod';

import { request } from '@/lib/api';

/* ---------------------------------------------------------------------------
   מרקטפלייס התהליכים (ADR 0003).
   --------------------------------------------------------------------------- */

export const jobLifecycleSettings = z.object({
  sources: z.array(z.enum(['EMAIL', 'MANUAL', 'CUSTOMER_LINK', 'QUOTE'])),
  autoAssign: z.boolean(),
  notifyOnCreate: z.boolean(),
  notifyOnClose: z.boolean(),
  notifyOnSchedule: z.boolean().default(true),
  monthlyBilling: z.boolean(),
  specificSources: z.array(
    z.object({ kind: z.enum(['EMAIL', 'PHONE']), value: z.string(), label: z.string().optional() }),
  ),
});
export type JobLifecycleSettings = z.infer<typeof jobLifecycleSettings>;

export const quoteRemindersSettings = z.object({
  scheduleDays: z.array(z.number()),
  message: z.string(),
});
export type QuoteRemindersSettings = z.infer<typeof quoteRemindersSettings>;

export const greenInvoiceBillingSettings = z.object({
  documentType: z.union([z.literal(305), z.literal(320)]),
  defaultMode: z.enum(['MONTHLY', 'PER_JOB']),
  vatMode: z.enum(['added', 'included', 'exempt']),
  emailClient: z.boolean(),
  autoIssueMax: z.number(),
});
export type GreenInvoiceBillingSettings = z.infer<typeof greenInvoiceBillingSettings>;

export const UNIT_KINDS = ['cabinet', 'wardrobe', 'shelf', 'drawer_unit', 'table', 'other'] as const;
export type UnitKind = (typeof UNIT_KINDS)[number];
export const COMPONENTS = ['material', 'edgeBand', 'hinge', 'slide', 'handle', 'labor'] as const;
export type Component = (typeof COMPONENTS)[number];

export const carpentrySettings = z.object({
  sheet: z.object({ widthCm: z.number(), heightCm: z.number() }),
  wastePct: z.number(),
  laborHours: z.record(z.enum(UNIT_KINDS), z.number()),
  laborHoursPerDrawer: z.number(),
  codes: z.record(z.enum(COMPONENTS), z.string().nullable()),
});
export type CarpentrySettings = z.infer<typeof carpentrySettings>;

export const contractRemindersSettings = z.object({ leadDays: z.number(), warrantyDays: z.number() });
export type ContractRemindersSettings = z.infer<typeof contractRemindersSettings>;

export const supplierInvoiceRemindersSettings = z.object({ leadDays: z.number(), approveAfterDays: z.number() });
export type SupplierInvoiceRemindersSettings = z.infer<typeof supplierInvoiceRemindersSettings>;

const base = {
  name: z.string(),
  tagline: z.string(),
  steps: z.array(z.string()),
  icon: z.enum(['workflow', 'bell', 'ruler', 'receipt']),
  verticals: z.array(z.string()),
  recommends: z.array(z.enum(['gmail', 'drive'])),
  requires: z.array(z.enum(['greenInvoice'])).default([]),
  status: z.enum(['available', 'coming_soon']),
  enabled: z.boolean(),
  enabledAt: z.string().nullable(),
  /** המודול של התהליך מוצג לעסק (lib/features.ts). false — לא מציגים. */
  relevant: z.boolean().default(true),
};

export const processSchema = z.discriminatedUnion('key', [
  z.object({ ...base, key: z.literal('job-lifecycle'), settings: jobLifecycleSettings }),
  z.object({ ...base, key: z.literal('quote-reminders'), settings: quoteRemindersSettings }),
  z.object({
    ...base,
    key: z.literal('green-invoice-billing'),
    settings: greenInvoiceBillingSettings.nullable(),
  }),
  z.object({
    ...base,
    key: z.literal('carpentry-drawing-quote'),
    settings: carpentrySettings.nullable(),
  }),
  z.object({ ...base, key: z.literal('contract-reminders'), settings: contractRemindersSettings.nullable() }),
  z.object({ ...base, key: z.literal('supplier-invoice-reminders'), settings: supplierInvoiceRemindersSettings.nullable() }),
]);
export type Process = z.infer<typeof processSchema>;

export const outboundSchema = z.object({
  id: z.string(),
  processKey: z.string(),
  channel: z.enum(['EMAIL', 'WHATSAPP']),
  // SCHEDULED — נדחתה לשעה מותרת (שעות שקט, שבת, קצב ללקוח). נשלחת לבד.
  status: z.enum(['PENDING', 'SCHEDULED', 'SENT', 'DISMISSED', 'FAILED']),
  recipient: z.string().nullable(),
  subject: z.string().nullable(),
  body: z.string(),
  waUrl: z.string().nullable(),
  reason: z.string(),
  error: z.string().nullable(),
  createdAt: z.string(),
  customer: z.object({ id: z.string(), name: z.string() }).nullable(),
});
export type OutboundMessage = z.infer<typeof outboundSchema>;

export const monthlySchema = z.object({
  period: z.object({ key: z.string(), label: z.string() }),
  isOpen: z.boolean(),
  customers: z.array(
    z.object({
      customer: z.object({
        id: z.string(),
        name: z.string(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
      }),
      tasks: z.number(),
      billableLines: z.number(),
      total: z.string(),
    }),
  ),
});
export type MonthlyOverview = z.infer<typeof monthlySchema>;

export const PROCESSES_KEY = ['processes'];
export const OUTBOUND_KEY = ['outbound-messages'];

export const processesApi = {
  list: (signal?: AbortSignal) => request('/processes', { schema: z.array(processSchema), signal }),
  update: (key: string, body: { enabled?: boolean; settings?: Record<string, unknown> }) =>
    request(`/processes/${key}`, { method: 'PUT', body, schema: processSchema }),

  queue: (signal?: AbortSignal) =>
    request('/outbound-messages', { schema: z.array(outboundSchema), signal }),
  settle: (id: string, action: 'sent' | 'dismiss') =>
    request(`/outbound-messages/${id}/${action}`, { method: 'POST' }),

  monthly: (period: string | undefined, signal?: AbortSignal) =>
    request(`/billing/monthly${period ? `?period=${period}` : ''}`, {
      schema: monthlySchema,
      signal,
    }),
  approveMonthly: (period: string, customerIds: string[]) =>
    request('/billing/monthly/approve', {
      method: 'POST',
      body: { period, customerIds },
      schema: z.object({
        results: z.array(
          z.object({
            customerId: z.string(),
            ok: z.boolean(),
            invoiceNumber: z.number().optional(),
            delivery: z.string().optional(),
            error: z.string().optional(),
          }),
        ),
        error: z.string().optional(),
      }),
    }),
};

/* ---------------------------------------------------------------------------
   הצעה משרטוט (נגרות).
   --------------------------------------------------------------------------- */

export const drawingUnitSchema = z.object({
  kind: z.enum(UNIT_KINDS),
  label: z.string().nullish(),
  widthCm: z.number(),
  heightCm: z.number(),
  depthCm: z.number(),
  qty: z.number(),
  doors: z.number(),
  drawers: z.number(),
  shelves: z.number(),
  notes: z.string().nullish(),
});
export type DrawingUnit = z.infer<typeof drawingUnitSchema>;

export const draftSchema = z.object({
  calc: z.object({
    sheets: z.number(),
    areaM2: z.number(),
    edgeBandM: z.number(),
    hinges: z.number(),
    slidePairs: z.number(),
    handles: z.number(),
    laborHours: z.number(),
  }),
  lines: z.array(
    z.object({
      component: z.enum(COMPONENTS),
      priceCode: z.string(),
      description: z.string(),
      qty: z.number(),
      unitPrice: z.string(),
      amount: z.string(),
    }),
  ),
  total: z.string(),
  missing: z.array(z.enum(COMPONENTS)),
  codes: z.record(z.enum(COMPONENTS), z.string().nullable()),
});
export type Draft = z.infer<typeof draftSchema>;

export const analysisSchema = z.object({
  units: z.array(drawingUnitSchema),
  confidence: z.number(),
  unclear: z.array(z.string()),
  fake: z.boolean().optional(),
});

export const carpentryApi = {
  analyze: (file: File, hint?: string) => {
    const body = new FormData();
    body.append('file', file);
    if (hint) body.append('hint', hint);
    return request('/processes/carpentry/drafts', {
      method: 'POST',
      body,
      schema: z.object({ analysis: analysisSchema, draft: draftSchema.nullable() }),
    });
  },
  price: (units: DrawingUnit[], codes: Partial<Record<Component, string | null>>) =>
    request('/processes/carpentry/price', {
      method: 'POST',
      body: { units, codes },
      schema: draftSchema,
    }),
};
